<template>
  <v-row justify="center">
    <!-- Main Serial Selector Dialog -->
    <v-dialog
      v-model="dialogStatus"
      persistent
      origin="top center"
      content-class="serial-selector-dialog"
      :max-width="dialogWidth"
    >
      <v-card>
        <!-- Header (System Theme) -->
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text d-flex align-center justify-space-between w-100"
        >
          <div class="d-flex align-center">
            <span class="font-weight-700">
              Serial Numbers : {{ serialObject?.product || 'Product Information' }}
              <span class="cyan--text" v-if="serialObject?.barcode">({{ serialObject?.barcode }})</span>
            </span>
          </div>
          <v-btn icon small @click="$emit('close:dialog')">
            <v-icon color="#000">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="px-6 py-4">
          <!-- 4 Stats Cards Row -->
          <v-row class="mb-2">
            <!-- Total Available -->
            <v-col cols="12" sm="6" md="3" class="py-1 px-2">
              <div class="stat-card stat-blue d-flex align-center pa-3">
                <div class="stat-icon-wrap stat-icon-blue mr-3 d-flex align-center justify-center">
                  <v-icon color="#2563eb" size="20">mdi-cube-outline</v-icon>
                </div>
                <div>
                  <div class="stat-label">Total Available</div>
                  <div class="stat-val stat-val-blue font-weight-700">{{ totalAvailableCount }}</div>
                </div>
              </div>
            </v-col>

            <!-- Selected -->
            <v-col cols="12" sm="6" md="3" class="py-1 px-2">
              <div class="stat-card stat-green d-flex align-center pa-3">
                <div class="stat-icon-wrap stat-icon-green mr-3 d-flex align-center justify-center">
                  <v-icon color="#16a34a" size="20">mdi-check-circle-outline</v-icon>
                </div>
                <div>
                  <div class="stat-label">Selected</div>
                  <div class="stat-val stat-val-green font-weight-700">{{ activeCount }}</div>
                </div>
              </div>
            </v-col>

            <!-- Required / Max -->
            <v-col cols="12" sm="6" md="3" class="py-1 px-2">
              <div class="stat-card stat-purple d-flex align-center pa-3">
                <div class="stat-icon-wrap stat-icon-purple mr-3 d-flex align-center justify-center">
                  <v-icon color="#9333ea" size="20">mdi-clipboard-text-outline</v-icon>
                </div>
                <div>
                  <div class="stat-label">Adjusted / Max</div>
                  <div class="stat-val stat-val-purple font-weight-700">{{ targetQty }}</div>
                </div>
              </div>
            </v-col>

            <!-- Progress -->
            <v-col cols="12" sm="6" md="3" class="py-1 px-2">
              <div class="stat-card stat-gray d-flex align-center pa-3">
                <div class="stat-progress-circle mr-3 d-flex align-center justify-center">
                  <span class="font-size-11 font-weight-700">{{ progressPercent }}%</span>
                </div>
                <div>
                  <div class="stat-label">Progress</div>
                  <div class="stat-progress-text" :class="isProgressComplete ? 'text-success-custom' : 'text-danger-custom'">
                    {{ activeCount }} of {{ targetQty }} selected
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Warning Alert for Unavailable Serials -->
          <div v-if="unavailableCount > 0 && isCalcType1" class="warning-banner d-flex align-center justify-space-between px-4 py-2 mb-3">
            <div class="d-flex align-center">
              <v-icon color="#d97706" class="mr-2" size="20">mdi-alert</v-icon>
              <span class="warning-text font-weight-600 font-size-13">
                {{ unavailableCount }} serial number(s) are unavailable.
                <span class="font-weight-400 grey--text text--darken-2">Only available serial numbers can be selected.</span>
              </span>
            </div>
          </div>

          <div v-if="isCalcType2 && targetQty > 5000" class="warning-banner d-flex align-center px-4 py-2 mb-3">
            <v-icon color="#d97706" class="mr-2" size="20">mdi-alert</v-icon>
            <span class="warning-text font-weight-600 font-size-13">
              Addition quantity cannot be greater than 5000 serial numbers.
            </span>
          </div>

          <!-- TYPE 1 : SUBTRACTION MODE -->
          <template v-if="isCalcType1">
            <!-- Toolbar: Auto Select & Search -->
            <div class="d-flex flex-wrap align-center justify-space-between mb-3 px-1">
              <div class="d-flex align-center">
                <v-checkbox
                  :input-value="isAllRequiredSelected"
                  @change="toggleAutoSelect"
                  color="cyan darken-2"
                  hide-details
                  class="mt-0 pt-0 mr-2"
                ></v-checkbox>
                <div>
                  <div class="font-weight-600 font-size-14 text-dark cursor-pointer user-select-none" @click="toggleAutoSelect">
                    Auto Select Available
                  </div>
                  <div class="caption grey--text">
                    Selects up to {{ targetQty }} available serial number(s).
                  </div>
                </div>
              </div>

              <div style="min-width: 280px;" class="mt-2 mt-sm-0">
                <v-text-field
                  v-model.trim="search"
                  clearable
                  dense
                  outlined
                  flat 
                  solo
                  hide-details
                  placeholder="Search Serial Number..."
                  prepend-inner-icon="mdi-magnify"
                  color="cyan"
                  class="search-input"
                ></v-text-field>
              </div>
            </div>

            <!-- Scrollable Serial Cards Grid -->
            <perfect-scrollbar
              :options="{ suppressScrollX: true }"
              class="scroll"
              style="max-height: 50vh; position: relative"
            >
              <v-row class="px-1" v-if="filteredSerialList && filteredSerialList.length > 0">
                <v-col
                  v-for="(row, rowindex) in filteredSerialList"
                  :key="rowindex"
                  cols="12"
                  sm="6"
                  md="3"
                  class="pa-2"
                >
                  <div
                    class="serial-card pa-3"
                    :class="{
                      'serial-card-selected': isSelected(row.id),
                      'serial-card-disabled': row.activated != 1
                    }"
                    @click="row.activated == 1 && selecteData(row)"
                  >
                    <!-- Card Top: Serial Text + Copy + QR Button -->
                    <div class="d-flex align-center justify-space-between mb-3">
                      <div class="d-flex align-center text-truncate mr-1">
                        <span class="serial-text font-weight-700 font-size-14 mr-1 text-truncate" :class="{ 'grey--text': row.activated != 1 }">
                          {{ row.text }}
                        </span>
                        <v-btn
                          icon
                          x-small
                          class="copy-btn ml-1"
                          @click.stop="copyNumber(row.text)"
                          title="Copy Serial Number"
                        >
                          <v-icon size="14" color="grey darken-1">mdi-content-copy</v-icon>
                        </v-btn>
                      </div>

                      <v-btn
                        icon
                        x-small
                        class="qr-btn"
                        @click.stop="openBarcodeDialog(row)"
                        title="View Barcode / QR"
                      >
                        <v-icon size="20" color="#0891b2">mdi-qrcode-scan</v-icon>
                      </v-btn>
                    </div>

                    <!-- Card Bottom: In Stock / Unavailable Badge -->
                    <div class="d-flex align-center justify-space-between">
                      <span v-if="row.activated == 1" class="in-stock-badge">In Stock</span>
                      <span v-else class="unavailable-badge">Unavailable</span>
                      <v-icon
                        v-if="isSelected(row.id)"
                        size="20"
                        color="#0891b2"
                      >
                        mdi-checkbox-marked-circle
                      </v-icon>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <!-- Empty State -->
              <div v-else class="text-center py-12">
                <img
                  height="48"
                  width="48"
                  :src="$assetURL('media/error/empty.png')"
                  class="mb-3 opacity-60"
                />
                <p class="m-0 font-weight-500 font-size-15 grey--text text--darken-1">
                  Uhh... There are no serial numbers at the moment.
                </p>
              </div>
            </perfect-scrollbar>
          </template>

          <!-- TYPE 2 : ADDITION MODE -->
          <template v-else-if="isCalcType2">
            <perfect-scrollbar
              :options="{ suppressScrollX: true }"
              class="scroll"
              style="max-height: 50vh; position: relative"
            >
              <v-card outlined elevation="0" class="mb-4 rounded-lg">
                <v-card-text class="pa-4">
                  <v-row class="mb-2 align-center">
                    <v-col md="8">
                      <label class="font-weight-700 font-size-15 color-custom-blue">Auto Generated?</label>
                      <RadioInputCustom
                        v-model="isAutoGenerated"
                        row
                        color="cyan"
                        :items="[
                          { label: 'Yes', value: 1 },
                          { label: 'No', value: 0 },
                        ]"
                        @change="resetAddedList"
                      ></RadioInputCustom>
                    </v-col>

                    <!-- IMPORT BUTTON -->
                    <v-col md="4" v-if="isManual" class="text-right">
                      <v-btn
                        outlined
                        color="cyan"
                        class="custom-bold-button"
                        small
                        @click="importDialog = true"
                      >
                        <v-icon left small>mdi-upload</v-icon>Import Excel
                      </v-btn>
                    </v-col>
                  </v-row>

                  <!-- PREFIX + NUMBER GENERATOR -->
                  <v-row class="mt-2 mb-3">
                    <v-col cols="12" md="4">
                      <label class="font-weight-600 font-size-14">Prefix</label>
                      <v-text-field
                        dense
                        filled
                        solo
                        flat
                        color="cyan"
                        placeholder="e.g. SN"
                        maxlength="8"
                        v-model.trim="serial_prefix"
                        hide-details
                      />
                    </v-col>

                    <v-col cols="12" md="4">
                      <label class="font-weight-600 font-size-14">Starting No.</label>
                      <v-text-field
                        type="text"
                        inputmode="numeric"
                        dense
                        filled
                        solo
                        flat
                        color="cyan"
                        placeholder="e.g. 1001"
                        maxlength="6"
                        v-model.trim="serial_prefix_no"
                        @keypress="isNumber"
                        @paste="handleStartingNumberPaste"
                        hide-details
                      />
                    </v-col>

                    <v-col cols="12" md="4">
                      <label class="font-weight-600 font-size-14">Preview</label>
                      <div class="d-flex align-center">
                        <span class="font-weight-700 font-size-15 text-uppercase color-custom-blue">
                          {{ serial_prefix || 'SN' }}-{{ serial_prefix_no || '1' }}
                        </span>
                        <v-btn
                          depressed
                          color="cyan"
                          class="custom-bold-button text-white ml-auto"
                          small
                          :disabled="!serial_prefix_no"
                          @click="generatePrefixes"
                        >
                          Generate
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- LIST OF GENERATED / IMPORTED SERIALS -->
                  <v-row v-if="added_searial_numbers.length" class="mb-2">
                    <v-col
                      v-for="(row, i) in added_searial_numbers"
                      :key="i"
                      cols="12"
                      sm="6"
                      md="3"
                    >
                      <div class="d-flex align-center">
                        <v-text-field
                          dense
                          outlined
                          hide-details
                          v-model="row.text"
                          :placeholder="'Serial #' + (i + 1)"
                          color="cyan"
                          class="mr-1"
                        />
                        <v-btn icon small color="red" @click="deleteNumber(i)">
                          <v-icon size="18">mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <div
                        v-if="!(row.text || '').trim()"
                        class="red--text font-size-12 mt-1"
                      >
                        Serial Number cannot be empty
                      </div>
                      <div
                        v-else-if="row.error || isDuplicateSerial(row.text)"
                        class="red--text font-size-12 mt-1"
                      >
                        Serial Number Already Exists
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </perfect-scrollbar>
          </template>
        </v-card-text>

        <!-- Footer (System Theme Buttons) -->
        <v-divider class="my-0"></v-divider>
        <v-card-actions class="px-6 py-3">
          <div class="d-flex align-center">
            <v-icon color="cyan" size="22" class="mr-2">mdi-check-circle</v-icon>
            <div>
              <div class="font-weight-700 font-size-14 custom-header-blue-text">
                {{ activeCount }} Selected
              </div>
              <div class="caption grey--text">
                {{ activeCount === 0 ? 'No serial numbers selected' : `${activeCount} of ${targetQty} selected` }}
              </div>
            </div>
          </div>

          <v-spacer></v-spacer>

          <v-btn
            depressed
            :disabled="!isSaveEnabled || serialLoading"
            :loading="serialLoading"
            class="mx-2 custom-bold-button white--text"
            color="cyan"
            @click="onDetailSubmit"
          >
            Save
          </v-btn>
          <v-btn
            depressed
            :disabled="serialLoading"
            class="mx-2 custom-grey-border custom-bold-button"
            @click="$emit('close:dialog')"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Barcode / QR Popup Dialog -->
    <v-dialog v-model="barcodeDialog" max-width="420" persistent content-class="barcode-popup-dialog">
      <v-card>
        <!-- Header -->
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text d-flex align-center justify-space-between w-100"
        >
          <span class="font-weight-700">Barcode</span>
          <v-btn icon small @click="barcodeDialog = false">
            <v-icon color="#000">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Preview Box -->
        <v-card-text class="px-6 py-4">
          <div class="barcode-preview-box pa-6 d-flex flex-column align-center justify-center">
            <img
              v-if="selectedRowBarcodeUrl"
              :src="selectedRowBarcodeUrl"
              style="max-width: 220px; max-height: 220px; object-fit: contain;"
              alt="Barcode/QR"
            />
            <div v-else class="text-center py-6">
              <v-icon size="96" color="#334155">mdi-qrcode</v-icon>
            </div>

            <div class="serial-pill-badge mt-4">
              {{ selectedRowSerialText }}
            </div>
          </div>
        </v-card-text>

        <v-divider class="my-0"></v-divider>
        <v-card-actions class="px-4 py-2">
          <v-spacer></v-spacer>
          <v-btn
            depressed
            class="mx-2 custom-grey-border custom-bold-button"
            @click="barcodeDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- IMPORT DIALOG -->
    <ImportTemplate
      :dialog="importDialog"
      title="Import Serial Number"
      sample-url="/import-samples/productbarcode.xlsx"
      api-url="serial-number/import"
      @close="importDialog = false"
      @response="getData"
    />
  </v-row>
</template>

<style scoped>
.v-dialog__content {
  align-items: start;
}

/* Stat Cards */
.stat-card {
  border-radius: 12px;
  transition: transform 0.15s ease;
}

.stat-blue {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
}

.stat-icon-blue {
  width: 36px;
  height: 36px;
  background: #e0f2fe;
  border-radius: 8px;
}

.stat-val-blue {
  color: #0284c7;
  font-size: 18px;
}

.stat-green {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.stat-icon-green {
  width: 36px;
  height: 36px;
  background: #dcfce7;
  border-radius: 8px;
}

.stat-val-green {
  color: #16a34a;
  font-size: 18px;
}

.stat-purple {
  background: #faf5ff;
  border: 1px solid #e9d5ff;
}

.stat-icon-purple {
  width: 36px;
  height: 36px;
  background: #f3e8ff;
  border-radius: 8px;
}

.stat-val-purple {
  color: #9333ea;
  font-size: 18px;
}

.stat-gray {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.stat-progress-circle {
  width: 36px;
  height: 36px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  color: #64748b;
}

.stat-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.2;
}

.text-success-custom {
  color: #16a34a;
  font-weight: 700;
  font-size: 13px;
}

.text-danger-custom {
  color: #dc2626;
  font-weight: 600;
  font-size: 13px;
}

/* Warning Banner */
.warning-banner {
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
}

.warning-text {
  color: #92400e;
}

/* Search input */
.search-input >>> .v-input__control > .v-input__slot {
  border-color: #cbd5e1 !important;
  min-height: 38px !important;
}

/* Serial Cards */
.serial-card {
  background: #f4fbf7;
  border: 1.5px solid #d1fae5;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.serial-card:hover {
  border-color: #059669;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.12);
  transform: translateY(-1px);
}

.serial-card-selected {
  background: #f0fdf4 !important;
  border: 2px solid #0891b2 !important;
  box-shadow: 0 4px 14px rgba(8, 145, 178, 0.18) !important;
}

.serial-card-disabled {
  background: #f1f5f9 !important;
  border: 1.5px solid #e2e8f0 !important;
  opacity: 0.6;
  cursor: not-allowed !important;
}

.serial-text {
  color: #166534;
}

.in-stock-badge {
  background: #15803d;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
}

.unavailable-badge {
  background: #94a3b8;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
}

.copy-btn, .qr-btn {
  background: transparent;
}

/* Barcode Modal */
.barcode-preview-box {
  background: #ffffff;
  border: 1.5px dashed #cbd5e1;
  border-radius: 16px;
}

.serial-pill-badge {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-weight: 700;
  font-size: 13px;
  border-radius: 20px;
  padding: 4px 22px;
}
</style>

<script>
import CommonMixin from "@/core/plugins/common-mixin";
import ImportTemplate from "@/view/pages/partials/Import-Template.vue";
import { ErrorEventBus, InitializeError, SuccessEventBus } from "@/core/lib/message.lib";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";
import RadioInputCustom from "@/view/components/RadioInputCustom.vue";

export default {
  name: "Serial-Number-Dialog",
  mixins: [CommonMixin],

  props: {
    serialArray: {
      type: Array,
      default() {
        return [];
      },
    },
    serialObject: {
      type: Object,
      default() {
        return {};
      },
    },
    dialogStatus: {
      type: Boolean,
      default: false,
    },
    parentId: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      search: null,
      importDialog: false,
      serialList: [],
      serialFormRff: true,
      serialLoading: false,
      enities: [],
      serial_prefix: null,
      serial_prefix_no: null,
      added_searial_numbers: [],
      isAutoGenerated: 1,
      barcodeDialog: false,
      selectedRowBarcodeUrl: null,
      selectedRowSerialText: null,
    };
  },

  watch: {
    serialArray: {
      immediate: true,
      deep: true,
      handler(val) {
        this.serialList = JSON.parse(JSON.stringify(val || []));
      },
    },

    dialogStatus: {
      immediate: true,
      handler(isOpen) {
        if (!isOpen) return;

        if (this.isCalcType1) {
          this.enities = Array.isArray(this.serialObject.adjust_serial_array)
            ? [...this.serialObject.adjust_serial_array]
            : [];
        }

        if (this.isCalcType2) {
          this.added_searial_numbers = Array.isArray(
            this.serialObject.adjust_serial_array
          )
            ? [...this.serialObject.adjust_serial_array]
            : [];

          if (!this.serial_prefix && !this.serial_prefix_no) {
            const firstSerial = this.added_searial_numbers[0]?.text || "";
            const match = String(firstSerial).match(/^(.*)-([0-9]+)$/);

            if (match) {
              this.serial_prefix = match[1];
              this.serial_prefix_no = match[2];
            }
          }
        }
      },
    },
  },

  computed: {
    dialogWidth() {
      return this.lodash.toSafeInteger((document.body.clientWidth / 100) * 60) || 880;
    },

    isCalcType1() {
      return this.serialObject?.calculation_type?.id === 1;
    },

    isCalcType2() {
      return this.serialObject?.calculation_type?.id === 2;
    },

    targetQty() {
      return Number(this.serialObject?.adjusted_quantity || 0);
    },

    activeCount() {
      return this.isCalcType1
        ? this.enities.length
        : this.added_searial_numbers.length;
    },

    totalAvailableCount() {
      return this.serialObject.initial_stock || this.serialList.filter((s) => s.activated == 1).length || 0;
    },

    unavailableCount() {
      return this.serialList.filter((s) => s.activated != 1).length;
    },

    progressPercent() {
      if (this.targetQty <= 0) return 0;
      const pct = Math.round((this.activeCount / this.targetQty) * 100);
      return Math.min(pct, 100);
    },

    isProgressComplete() {
      return this.activeCount === this.targetQty && this.targetQty > 0;
    },

    isAllRequiredSelected() {
      return this.isProgressComplete;
    },

    isSaveEnabled() {
      const hasEmptySerials =
        this.isCalcType2 &&
        this.added_searial_numbers.some(
          (serial) => !(serial.text || "").trim()
        );
      const hasDuplicateSerials =
        this.isCalcType2 &&
        this.added_searial_numbers.some((serial) =>
          this.isDuplicateSerial(serial.text)
        );
      const exceedsAdditionLimit = this.isCalcType2 && this.targetQty > 5000;

      return (
        this.activeCount === this.targetQty &&
        this.targetQty > 0 &&
        !hasEmptySerials &&
        !hasDuplicateSerials &&
        !exceedsAdditionLimit
      );
    },

    filteredSerialList() {
      if (!this.search) return this.serialList;
      const q = this.search.toLowerCase();
      return this.serialList.filter(
        (s) => s.text && s.text.toLowerCase().includes(q)
      );
    },

    isManual() {
      return this.isAutoGenerated == 0;
    },

    duplicateSerials() {
      return this.added_searial_numbers.reduce((duplicates, serial) => {
        const value = (serial.text || "").trim().toLowerCase();
        if (!value) return duplicates;
        duplicates[value] = (duplicates[value] || 0) + 1;
        return duplicates;
      }, {});
    },
  },

  methods: {
    handleStartingNumberPaste(event) {
      const pastedValue = event.clipboardData
        ? event.clipboardData.getData("text")
        : "";
      const numericValue = pastedValue.replace(/\D/g, "").slice(0, 6);

      event.preventDefault();
      this.serial_prefix_no = numericValue;
    },

    isDuplicateSerial(value) {
      const normalized = (value || "").trim().toLowerCase();
      return Boolean(normalized && this.duplicateSerials[normalized] > 1);
    },

    isSelected(id) {
      return this.enities.includes(id);
    },

    selecteData(row) {
      if (!row || row.activated != 1) return;

      const idx = this.enities.indexOf(row.id);

      if (idx !== -1) {
        this.enities.splice(idx, 1);
      } else {
        if (this.enities.length >= this.targetQty) {
          ErrorEventBus.$emit(
            "update:error",
            InitializeError(
              `You cannot select more than ${this.targetQty} serial number(s) as adjusted quantity is ${this.targetQty}.`
            )
          );
          return;
        }
        this.enities.push(row.id);
      }
    },

    toggleAutoSelect() {
      if (this.isAllRequiredSelected) {
        this.enities = [];
      } else {
        this.autoSelect();
      }
    },

    autoSelect() {
      if (this.targetQty <= 0) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Please enter a valid quantity greater than 0.")
        );
        return;
      }

      const activeSerials = this.serialList.filter((s) => s.activated == 1);
      if (!activeSerials.length) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("No serial numbers available to select.")
        );
        return;
      }

      const availableToPick = activeSerials.slice(0, this.targetQty);
      this.enities = availableToPick.map((item) => item.id);
      if (this.enities.length < this.targetQty) {
        ErrorEventBus.$emit(
          "update:warning",
          `Only ${this.enities.length} active serial numbers are available for selection.`
        );
      }
    },

    copyNumber(data) {
      if (!data) return;
      navigator.clipboard.writeText(data);
      SuccessEventBus.$emit("update:success", `${data} successfully copied.`);
    },

    openBarcodeDialog(row) {
      this.selectedRowSerialText = row.text || "N/A";
      this.selectedRowBarcodeUrl = row.barcode_image || null;
      this.barcodeDialog = true;
    },

    getData(data) {
      this.added_searial_numbers = this.lodash.cloneDeep(data);
    },

    resetAddedList() {
      this.added_searial_numbers = [];
    },

    resetManualFields() {
      this.added_searial_numbers = [];
      this.serial_prefix = null;
      this.serial_prefix_no = null;
    },

    generatePrefixes() {
      const adjusted = this.serialObject?.adjusted_quantity;

      if (!this.serial_prefix_no || adjusted <= 0) return;

      if (this.isCalcType2 && Number(adjusted) > 5000) {
        ErrorEventBus.$emit(
          "update:error",
          InitializeError("Addition quantity cannot be greater than 5000 serial numbers.")
        );
        return;
      }

      this.added_searial_numbers = [];

      for (let i = 1; i <= adjusted; i++) {
        const prefix = this.serial_prefix ? `${this.serial_prefix}-` : "";
        const num = String(Number(this.serial_prefix_no) + i - 1).padStart(
          this.serial_prefix_no.length,
          "0"
        );

        this.added_searial_numbers.push({
          uuid: uuidv4(),
          text: `${prefix}${num}`,
          editable: true,
          error: false,
        });
      }
    },

    deleteNumber(i) {
      this.added_searial_numbers.splice(i, 1);
    },

    onDetailSubmit() {
      const adjustedQty = Number(this.serialObject.adjusted_quantity);
      const calcType = this.serialObject?.calculation_type?.id;

      const activeList =
        calcType === 1 ? this.enities : this.added_searial_numbers;

      if (activeList.length !== adjustedQty) {
        return ErrorEventBus.$emit(
          "update:error",
          InitializeError(
            "Ensure that the adjusted quantity entered matches the Serial Number count"
          )
        );
      }

      if (calcType === 2 && adjustedQty > 5000) {
        return ErrorEventBus.$emit(
          "update:error",
          InitializeError("Addition quantity cannot be greater than 5000 serial numbers.")
        );
      }

      if (
        calcType === 2 &&
        activeList.some((serial) => this.isDuplicateSerial(serial.text))
      ) {
        return ErrorEventBus.$emit(
          "update:error",
          InitializeError("Serial Number Already Exists")
        );
      }

      this.$emit("save", cloneDeep(activeList));
    },

    isNumber(e) {
      if (e.key < "0" || e.key > "9") e.preventDefault();
    },
  },

  components: { ImportTemplate, RadioInputCustom },
};
</script>
